import { useMemo, useEffect, useState } from 'react'
import { useSiteConfig } from '../context/SiteConfigContext'
import { apiGet } from './api'
import type { Category } from '@buddys-choice/shared'

export interface StoreCategory {
  slug: string
  label: string
  navLabel?: string
  imageUrl?: string | null
}

export const DEFAULT_STORE_CATEGORIES: StoreCategory[] = [
  { slug: 'RINGS', label: 'Rings & Solitaires', navLabel: 'Gold Plated Rings' },
  { slug: 'NECKLACES', label: 'Temple & Antique Harams', navLabel: 'Temple Necklaces' },
  { slug: 'BRACELETS', label: 'CZ & Fashion Bracelets', navLabel: 'Gold Plated Bracelets' },
  { slug: 'WATCHES', label: 'Fashion Watches', navLabel: 'Fashion Watches' },
  { slug: 'EARRINGS', label: 'Pearl & Jhumka Earrings', navLabel: 'Gold Plated Earrings' },
]

export interface FooterCareLink {
  label: string
  href: string
}

export const DEFAULT_FOOTER_CARE_LINKS: FooterCareLink[] = [
  { label: 'Track Your Live Order', href: '/track' },
  { label: '1-Year Colour Replacement Claim', href: '/quality' },
  { label: 'COD & Insured Shipping Terms', href: '/quality' },
  { label: 'Gold Plated Jewellery Care Guide', href: '/quality' },
]

export function parseJsonArray<T>(json: string | undefined, fallback: T[]): T[] {
  if (!json?.trim()) return fallback
  try {
    const parsed = JSON.parse(json) as unknown
    if (Array.isArray(parsed) && parsed.length > 0) return parsed as T[]
  } catch {
    /* invalid JSON — use defaults */
  }
  return fallback
}

function mapCategoryToStore(cat: Category): StoreCategory {
  return {
    slug: cat.slug,
    label: cat.name,
    ...(cat.navLabel ? { navLabel: cat.navLabel } : {}),
    imageUrl: cat.imageUrl ?? null,
  }
}

function mapJsonCategory(item: Record<string, unknown>): StoreCategory | null {
  const slug = typeof item.slug === 'string' ? item.slug.toUpperCase() : ''
  if (!slug) return null
  const navLabel = typeof item.navLabel === 'string' ? item.navLabel : undefined
  const imageUrl = typeof item.imageUrl === 'string' ? item.imageUrl : undefined
  return {
    slug,
    label: typeof item.label === 'string' ? item.label : slug,
    ...(navLabel ? { navLabel } : {}),
    ...(imageUrl ? { imageUrl } : {}),
  }
}

/** Active categories from D1 API (storefront + admin product dropdowns) */
export function useStoreCategories(): { categories: StoreCategory[]; loading: boolean } {
  const { config } = useSiteConfig()
  const [apiCategories, setApiCategories] = useState<StoreCategory[] | null>(null)

  const legacyCategories = useMemo(() => {
    const parsed = parseJsonArray<Record<string, unknown>>(
      config.categories_json,
      DEFAULT_STORE_CATEGORIES as unknown as Record<string, unknown>[],
    )
    return parsed
      .map((item) => mapJsonCategory(item))
      .filter((c): c is StoreCategory => c !== null)
  }, [config.categories_json])

  useEffect(() => {
    apiGet<Category[]>('categories')
      .then((rows) => {
        if (Array.isArray(rows) && rows.length > 0) {
          setApiCategories(rows.map(mapCategoryToStore))
        } else {
          setApiCategories([])
        }
      })
      .catch(() => setApiCategories(null))
  }, [])

  const categories = useMemo(() => {
    if (apiCategories && apiCategories.length > 0) return apiCategories
    if (apiCategories && apiCategories.length === 0 && legacyCategories.length > 0) {
      return legacyCategories
    }
    if (apiCategories === null) return legacyCategories.length > 0 ? legacyCategories : DEFAULT_STORE_CATEGORIES
    return DEFAULT_STORE_CATEGORIES
  }, [apiCategories, legacyCategories])

  return { categories, loading: apiCategories === null }
}

/** Admin: all categories including inactive, with product counts */
export function useAdminCategories(): { categories: Category[]; loading: boolean; refresh: () => void } {
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)

  const refresh = () => {
    setLoading(true)
    apiGet<Category[]>('categories/manage')
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch(() => setCategories([]))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    refresh()
  }, [])

  return { categories, loading, refresh }
}

export function useFooterCareLinks(): FooterCareLink[] {
  const { config } = useSiteConfig()
  return useMemo(
    () => parseJsonArray(config.footer_care_links_json, DEFAULT_FOOTER_CARE_LINKS),
    [config.footer_care_links_json],
  )
}

export function getCategoryLabel(slug: string, categories: StoreCategory[]): string {
  const normalized = slug.toUpperCase()
  return categories.find((c) => c.slug === normalized)?.label ?? slug
}

/** Categories that have at least one active product in the catalogue */
export function useCategoriesWithProducts(): { categories: StoreCategory[]; loading: boolean } {
  const { categories: allCategories, loading: categoriesLoading } = useStoreCategories()
  const [activeSlugs, setActiveSlugs] = useState<string[] | null>(null)

  useEffect(() => {
    apiGet<string[]>('products/categories')
      .then((slugs) => setActiveSlugs(Array.isArray(slugs) ? slugs : []))
      .catch(() => setActiveSlugs([]))
  }, [])

  const categories = useMemo(() => {
    if (activeSlugs === null) return []
    const slugSet = new Set(activeSlugs.map((s) => s.toUpperCase()))
    return allCategories.filter((c) => slugSet.has(c.slug.toUpperCase()))
  }, [allCategories, activeSlugs])

  return { categories, loading: categoriesLoading || activeSlugs === null }
}
